import { Badge, Card, Table } from 'react-bootstrap';
import type { Row } from '../api/types';
import { Amount } from '../ui/format';

interface Statement {
  currency: string;
  name: string | null;
  lines: Row[];
  difference: Row | undefined;
}

/** The report returns four rows per currency (two lines, the totals, the difference), kept together and in currency order. */
export function groupByCurrency(rows: Row[]): Statement[] {
  const out: Statement[] = [];
  for (const r of rows) {
    let s = out[out.length - 1];
    if (!s || s.currency !== r.currency) {
      s = { currency: String(r.currency), name: r.currencyName ?? null, lines: [], difference: undefined };
      out.push(s);
    }
    if (r.rowKind === 'difference') s.difference = r;
    else s.lines.push(r);
  }
  return out;
}

/**
 * Business-overview Balance Sheet: one statement per currency, Assets on the left and Liabilities + Equity on the
 * right so the two sides line up row by row. Amounts are never converted between currencies.
 */
export function BalanceSheetView({ rows }: { rows: Row[] }) {
  const statements = groupByCurrency(rows);
  if (!statements.length) return <div className="text-center text-body-secondary py-4">No balances yet</div>;
  return (
    <>
      <p className="small text-body-secondary mb-3">
        Each currency is its own statement and nothing is converted between currencies. <strong>Assets</strong> are what the business holds
        or is owed (bank balances, agent receivables). <strong>Liabilities + Equity</strong> are what it owes (dispatcher payables) plus
        its retained profit. This is a running-balance report, so the two sides do not have to be equal.
      </p>
      <div className="d-grid gap-3">
        {statements.map((s) => {
          const diff = s.difference?.assetAmount as string | undefined;
          const balanced = diff !== undefined && Number(diff) === 0;
          return (
            <Card key={s.currency} className="mms-balance-statement">
              <Card.Header className="d-flex flex-wrap align-items-center gap-2">
                <strong className="fs-5">{s.currency}</strong>
                {s.name && <span className="text-body-secondary">{s.name}</span>}
              </Card.Header>
              <Card.Body className="p-2">
                <Table size="sm" className="mb-0 align-middle" aria-label={`${s.currency} balance sheet`}>
                  <thead>
                    <tr className="text-uppercase small text-body-secondary">
                      <th colSpan={2} className="fw-semibold">Assets</th>
                      <th colSpan={2} className="fw-semibold border-start ps-3">Liabilities + Equity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.lines.map((r) => {
                      const total = r.rowKind === 'total';
                      return (
                        <tr key={`${r.assetLine}`} className={total ? 'fw-bold border-top' : undefined}>
                          <td>{r.assetLine}</td>
                          <td className="text-end"><Amount value={r.assetAmount} /></td>
                          <td className="border-start ps-3">{r.liabilityLine}</td>
                          <td className="text-end"><Amount value={r.liabilityAmount} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card.Body>
              {diff !== undefined && (
                <Card.Footer className="d-flex flex-wrap align-items-center gap-2 small">
                  <span className="text-body-secondary">Difference (Assets − Liabilities + Equity)</span>
                  <span className="fw-semibold"><Amount value={diff} code={s.currency} /></span>
                  <Badge bg={balanced ? 'success-subtle' : 'warning-subtle'} text={balanced ? 'success-emphasis' : 'warning-emphasis'} className="fw-normal">
                    {balanced ? 'Balanced' : 'Not equal'}
                  </Badge>
                </Card.Footer>
              )}
            </Card>
          );
        })}
      </div>
    </>
  );
}
